import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  TextField,
  Button,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Helmet } from 'react-helmet-async';
import SendIcon from '@mui/icons-material/Send';
import Header from '../components/Public/Header';
import Footer from '../components/Public/Footer';
import { SITE } from '../config/site';

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess(false);

    try {
      // In a real implementation, this would POST to /api/contact
      // For now, we'll just simulate success
      await new Promise((resolve) => setTimeout(resolve, 1000));
      
      // Track analytics event
      if (window.gtag) {
        window.gtag('event', 'contact_submit', { success: true });
      }

      setSuccess(true);
      setFormData({ name: '', email: '', message: '' });
    } catch (err) {
      setError('Failed to send message. Please try again or email us directly.');
      if (window.gtag) {
        window.gtag('event', 'contact_submit', { success: false });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Contact Us - {SITE.name}</title>
        <meta
          name="description"
          content={`Get in touch with ${SITE.name}. We're here to help with any questions.`}
        />
        <link rel="canonical" href={`https://${SITE.domain}/contact`} />
      </Helmet>

      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        <Header />
        <Box component="main" sx={{ flexGrow: 1, py: 8 }}>
          <Container maxWidth="sm">
            <Paper elevation={2} sx={{ p: 4 }}>
              <Typography variant="h3" component="h1" gutterBottom>
                Contact Us
              </Typography>
              <Typography variant="body1" color="text.secondary" paragraph>
                Have a question or need help? Send us a message and we'll get back to you
                as soon as possible.
              </Typography>

              {success && (
                <Alert severity="success" sx={{ mb: 3 }}>
                  Thank you for your message! We'll get back to you soon.
                </Alert>
              )}

              {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  {error}
                </Alert>
              )}

              <Box component="form" onSubmit={handleSubmit} noValidate>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="name"
                  label="Name"
                  name="name"
                  autoComplete="name"
                  value={formData.name}
                  onChange={handleChange}
                  disabled={loading}
                />
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="email"
                  label="Email Address"
                  name="email"
                  autoComplete="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  disabled={loading}
                />
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="message"
                  label="Message"
                  name="message"
                  multiline
                  rows={6}
                  value={formData.message}
                  onChange={handleChange}
                  disabled={loading}
                  inputProps={{ maxLength: 1000 }}
                  helperText={`${formData.message.length}/1000 characters`}
                />
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  endIcon={loading ? <CircularProgress size={20} /> : <SendIcon />}
                  disabled={loading || !formData.name || !formData.email || !formData.message}
                  sx={{ mt: 3 }}
                >
                  {loading ? 'Sending...' : 'Send Message'}
                </Button>
              </Box>

              <Box sx={{ mt: 4, pt: 4, borderTop: 1, borderColor: 'divider' }}>
                <Typography variant="body2" color="text.secondary">
                  You can also reach us directly at:{' '}
                  <Typography
                    component="a"
                    href={`mailto:${SITE.supportEmail}`}
                    sx={{ color: 'primary.main', textDecoration: 'none' }}
                  >
                    {SITE.supportEmail}
                  </Typography>
                </Typography>
              </Box>
            </Paper>
          </Container>
        </Box>
        <Footer />
      </Box>
    </>
  );
}
